import { Stack, Box, Typography, Avatar, Paper } from "@mui/material";
import type {
  OrganizationShowQueryType,
  FeeTypeType,
  ProviderFeePayerType,
} from "@/types/graphql/admin.generated";
import { CustomCellDate } from "@/shared/components";
import { SudoButton } from "@/admin/shared/components/SudoButton";
import { OrganizationCurrentContracts } from "./OrganizationCurrentContracts";

interface OrganizationDetailsProps {
  organization: OrganizationShowQueryType["organization"];
}

type FeeSettings = NonNullable<
  OrganizationShowQueryType["organization"]
>["feeSettings"];

const PROVIDER_FEE_PAYER_LABEL: Record<ProviderFeePayerType, string> = {
  ORGANIZATION: "организация",
  OTTKIT: "платформа",
  IN_HALF: "пополам",
};

function formatFeeSettings(feeSettings: FeeSettings): string {
  const { feeType, percentageFee, fixedFeeAmount } = feeSettings;
  const percentagePart = `${percentageFee} %`;
  const fixedPart = `фиксированная ${fixedFeeAmount.value} ${fixedFeeAmount.currency}`;

  const byType: Record<FeeTypeType, string> = {
    PERCENTAGE: percentagePart,
    FIXED: fixedPart,
    FIXED_PERCENTAGE_SUM: `${fixedFeeAmount.value} ${fixedFeeAmount.currency} + ${percentageFee} %`,
  };

  return byType[feeType];
}

export function OrganizationDetails({
  organization,
}: OrganizationDetailsProps) {
  if (!organization) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Организация не найдена
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Avatar src={organization.logo?.url} sx={{ width: 52, height: 52 }}>
            {organization.title.slice(0, 2).toUpperCase()}
          </Avatar>
          <Typography variant="h5">{organization.title}</Typography>
        </Stack>

        {organization.sudoUrl && (
          <Box>
            <SudoButton href={organization.sudoUrl} label="Войти в кабинет" />
          </Box>
        )}

        <Typography variant="h6">Финансы</Typography>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Валюта отчётов
          </Typography>
          <Typography variant="body1">{organization.currency}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Комиссия платформы
          </Typography>
          <Typography variant="body1">
            {formatFeeSettings(organization.feeSettings)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Комиссию провайдера платит
          </Typography>
          <Typography variant="body1">
            {
              PROVIDER_FEE_PAYER_LABEL[
                organization.feeSettings.providerFeePayer
              ]
            }
          </Typography>
        </Box>

        <Typography variant="h6">Контракты</Typography>

        <OrganizationCurrentContracts organizationId={organization.id} />

        <Typography variant="h6">Системная информация</Typography>

        <Box>
          <Typography variant="body2" color="text.secondary">
            ID
          </Typography>
          <Typography variant="body1">{organization.id}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата создания
          </Typography>
          <CustomCellDate date={organization.createdAt} />
        </Box>

        {organization.updatedAt && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Дата обновления
            </Typography>
            <CustomCellDate date={organization.updatedAt} />
          </Box>
        )}

        {organization.deletedAt && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Дата удаления
            </Typography>
            <CustomCellDate date={organization.deletedAt} />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
